import React from 'react';
import { FaKey, FaLock, FaEyeSlash } from 'react-icons/fa';

/**
 * A key with a slash through it: the key is taken away. Font Awesome 4/5 has no
 * such glyph, so it is composed. Accepts the same props the react-icons
 * components do, so it can sit in KEY_VISUALS beside them.
 */
export const KeyRemovedIcon = ({ size = 12, color = 'currentColor', className, title, style, ...rest }) => (
    <span
        className={className}
        title={title}
        role={title ? 'img' : undefined}
        aria-label={title}
        aria-hidden={rest['aria-hidden']}
        style={{
            position: 'relative',
            display: 'inline-flex',
            width: size,
            height: size,
            ...style,
        }}
    >
        <FaKey size={size} color={color} aria-hidden="true" />
        <span
            aria-hidden="true"
            style={{
                position: 'absolute',
                left: '50%',
                top: '-12%',
                width: Math.max(1.5, size / 7),
                height: '124%',
                borderRadius: 1,
                backgroundColor: color,
                transform: 'translateX(-50%) rotate(45deg)',
            }}
        />
    </span>
);

/**
 * The ways a key shows up in a scenario, and how each one looks.
 *
 * Everything that draws a key — the canvas badges, the edge strokes, the edge
 * label icons, the sidebar chips, the legend — reads from here, so the legend
 * can never disagree with the canvas it explains.
 *
 * The two gate roles differ by dash pattern and icon as well as colour, so they
 * stay distinguishable for anyone who cannot tell blue from magenta. Picking up
 * and taking away change what a player holds, not who sees a choice, so they
 * leave the edge stroke alone.
 */
export const KEY_ROLES = Object.freeze({
    GRANT: 'grant',
    REMOVE: 'remove',
    HAS: 'has',
    LACKS: 'lacks',
});

export const KEY_VISUALS = Object.freeze({
    grant: {
        Icon: FaKey,
        color: '#b8860b',
        bg: '#fff8e1',
        dash: null,
        label: 'Players pick up the key here',
    },
    remove: {
        Icon: KeyRemovedIcon,
        color: '#475569',
        bg: '#eef2f6',
        dash: null,
        label: 'Players lose the key here',
    },
    has: {
        Icon: FaLock,
        color: '#1d4ed8',
        bg: '#e8eefc',
        dash: '6 4',
        label: 'Choice only visible to players who HAVE the key',
    },
    lacks: {
        Icon: FaEyeSlash,
        color: '#c2185b',
        bg: '#fde8ef',
        dash: '2 3',
        label: 'Choice only visible to players who DO NOT have the key',
    },
});

/** How faded the canvas is outside the key being inspected. */
export const DIM_OPACITY = 0.18;
